import moment from "moment";
import pdfMake from "pdfmake/build/pdfmake";
import pdfFonts from "pdfmake/build/vfs_fonts";
import {
  texto,
  numero,
  moneda,
  fecha,
  centro,
  construirHojaTabla,
  limpiarNombreArchivo,
  paraPdf,
  descargarLibro,
} from "../../../../Components/ExcelTablaReporte";
import reporteFiscalPdf from "../../../../Pdf/reporteFiscalPdf";

pdfMake.vfs = pdfFonts.pdfMake.vfs;

/**
 * Excel y PDF del Reporte Costo Vs Ultima Compra.
 *
 * Una sola tabla, armada una vez, que alimenta los dos archivos para que no
 * puedan decir cosas distintas (mismo patron que
 * `ReporteFacturasGlobales/exportarFacturasGlobales.js`).
 */

// El orden sigue la plantilla que armo el cliente: el costo del catalogo, su
// conversion a MXN, y junto a ellos la diferencia con su Desviacion.
const COLUMNAS = [
  { titulo: "Codigo", tipo: "texto", ancho: 14, enPdf: false },
  { titulo: "Clave", tipo: "texto", ancho: 14, anchoPdf: 54 },
  { titulo: "Descripcion", tipo: "texto", ancho: 42, anchoPdf: 120 },
  { titulo: "Unidad", tipo: "texto", ancho: 10, enPdf: false },
  { titulo: "Familia", tipo: "texto", ancho: 20, enPdf: false },
  { titulo: "Marca", tipo: "texto", ancho: 18, enPdf: false },
  { titulo: "Costo Catalogo", tipo: "moneda", ancho: 16, anchoPdf: 64 },
  { titulo: "Moneda", tipo: "texto", ancho: 9, enPdf: false },
  { titulo: "T.C. Factura", tipo: "numero", ancho: 12, anchoPdf: 40 },
  { titulo: "Costo Catalogo MXN", tipo: "moneda", ancho: 20, anchoPdf: 64 },
  { titulo: "Ultimo Costo Compra", tipo: "moneda", ancho: 20, anchoPdf: 64 },
  { titulo: "Diferencia", tipo: "moneda", ancho: 15, anchoPdf: 62 },
  { titulo: "Diferencia %", tipo: "numero", ancho: 13, anchoPdf: 48 },
  { titulo: "Desviacion", tipo: "texto", ancho: 22, enPdf: false },
  { titulo: "Fecha Ultima Compra", tipo: "fecha", ancho: 15, anchoPdf: 56 },
  { titulo: "Dias", tipo: "numero", ancho: 8, enPdf: false },
  { titulo: "Proveedor", tipo: "texto", ancho: 32, anchoPdf: 96 },
  { titulo: "Documento", tipo: "texto", ancho: 20, enPdf: false },
  { titulo: "Compras", tipo: "numero", ancho: 10, enPdf: false },
  { titulo: "Costo Prom. Compras", tipo: "moneda", ancho: 20, enPdf: false },
  { titulo: "Fecha Calculo Costo", tipo: "fecha", ancho: 15, enPdf: false },
  { titulo: "Dias Desviacion", tipo: "numero", ancho: 12, enPdf: false },
  { titulo: "Origen T.C.", tipo: "texto", ancho: 18, enPdf: false },
];

/** Los mismos umbrales que pintan el semaforo de la tabla en pantalla. */
export const UMBRAL_ALTO = 15;
export const UMBRAL_MEDIO = 5;

export const etiquetaDesviacion = (registro) => {
  if (registro.diferenciaPct === null || registro.diferenciaPct === undefined) {
    return "Sin costo en catalogo";
  }
  if (registro.diferencia > 0) return "Compramos mas caro";
  if (registro.diferencia < 0) return "Compramos mas barato";
  return "Alineado";
};

// El PDF si acepta un color por celda (`reporteFiscalPdf` lo respeta); el Excel
// no, y ahi el sentido lo carga la columna "Desviacion".
const rellenoPdf = (diferenciaPct) => {
  if (diferenciaPct === null || diferenciaPct === undefined) return "#FFCCC7";
  const magnitud = Math.abs(diferenciaPct);
  if (magnitud >= UMBRAL_ALTO) return "#FFCCC7";
  if (magnitud >= UMBRAL_MEDIO) return "#FFE7BA";
  return null;
};

export const construirTabla = (registros) => {
  const filas = (registros || []).map((registro) => {
    const celdaPct =
      registro.diferenciaPct === null || registro.diferenciaPct === undefined
        ? centro("-")
        : numero(registro.diferenciaPct);
    const relleno = rellenoPdf(registro.diferenciaPct);
    // Solo los insumos en moneda extranjera llevan T.C.; en los MXN el costo del
    // catalogo ya es el que se compara y las dos columnas van vacias.
    const conTC = registro.tipoDeCambio !== null && registro.tipoDeCambio !== undefined;

    return [
      texto(registro.codigoInsumo),
      texto(registro.claveInsumo),
      texto(registro.descripcionInsumo),
      texto(registro.nombreUnidad),
      texto(registro.nombreFamilia),
      texto(registro.nombreMarca),
      moneda(registro.costoCatalogo),
      texto(registro.abreviacionMoneda),
      conTC ? numero(registro.tipoDeCambio) : texto(""),
      conTC ? moneda(registro.costoCatalogoMxn) : texto(""),
      moneda(registro.ultimoCosto),
      relleno ? { ...moneda(registro.diferencia), fillColor: relleno } : moneda(registro.diferencia),
      relleno ? { ...celdaPct, fillColor: relleno } : celdaPct,
      texto(etiquetaDesviacion(registro)),
      fecha(registro.fechaUltimaCompra),
      numero(registro.diasDesdeUltimaCompra),
      texto(registro.proveedor),
      texto(registro.documento),
      numero(registro.comprasConsideradas),
      moneda(registro.costoPromedioCompras),
      fecha(registro.fechaCalculoCosto),
      numero(registro.diasDesviacion),
      texto(registro.origenTipoDeCambio),
    ];
  });

  // Sin `totalizar`: aqui todo el dinero es COSTO UNITARIO, y una fila de
  // totales sumaria costos unitarios de insumos distintos, que no significa
  // nada. Con los totales en null, `construirHojaTabla` omite la fila.
  return { columnas: COLUMNAS, filas, totales: COLUMNAS.map(() => null) };
};

const TITULO = "Costo del catalogo vs ultima compra";

const nombreArchivo = (empresaNombre) =>
  `Costo_Vs_Ultima_Compra_${limpiarNombreArchivo(empresaNombre)}_${moment().format("YYYY-MM-DD")}`;

const indicadoresDe = (resumen = {}) => [
  { etiqueta: "Insumos analizados", valor: String(resumen.insumosAnalizados || 0) },
  { etiqueta: "Mostrados", valor: String(resumen.insumosMostrados || 0) },
  { etiqueta: "Compramos mas caro", valor: String(resumen.comprandoMasCaro || 0) },
  { etiqueta: "Compramos mas barato", valor: String(resumen.comprandoMasBarato || 0) },
  { etiqueta: "Desviacion promedio", valor: `${resumen.desviacionPromedio || 0}%` },
];

export const descargarExcel = ({ datos, empresaNombre, descripcionFiltros }) => {
  descargarLibro(
    [
      {
        nombre: "Costo Vs Ultima Compra",
        hoja: construirHojaTabla(construirTabla(datos), {
          titulo: TITULO,
          empresaNombre,
          descripcionFiltros,
        }),
      },
    ],
    nombreArchivo(empresaNombre)
  );
};

export const descargarPdf = ({ datos, resumen, empresaNombre, descripcionFiltros }) => {
  const tabla = construirTabla(datos);

  pdfMake
    .createPdf(
      reporteFiscalPdf({
        titulo: "Reporte Costo Vs Ultima Compra",
        empresaNombre,
        descripcionFiltros,
        indicadores: indicadoresDe(resumen),
        contador: tabla.filas.length,
        secciones: [{ titulo: TITULO, ...paraPdf(tabla) }],
      })
    )
    .download(`${nombreArchivo(empresaNombre)}.pdf`);
};
